import { FC } from "react";

import {GetServerSideProps, InferGetServerSidePropsType as Infer} from "next";
import { useTranslation } from "next-i18next";

import { FullWidthVStack } from "@components/common";
import LayoutHead from "@components/PageHeader/LayoutHead";
import responsiveStyle from "@lib/theme/responsiveStyle";
import withPropsMiddleware from "@lib/withPropsMiddleware";
import { loadBanners, Banners } from "@modules/banners";
import {FaqPageList, isFaqPageNotFound, loadFaq, TFaq} from "@modules/faq";
import { loadFeatures } from "@modules/features";
import Layout, { TWithLayout, LayoutContent } from "@modules/layout";
import { ROUTES } from "@utils/constants";
import getEnv from "@utils/getEnv";
import {IBanners} from "@modules/banners/models/banners";
import {TListWithPagination} from "@lib/pagination";

type TWithLocales = {
    defaultLocale: string;
    customLocales: string[];
    customLocale: string; // Change from string? to string
    features: string[] | null;
    banners: IBanners | null;
    faq: TListWithPagination<TFaq> | null;
};

export const getServerSideProps: GetServerSideProps<TWithLocales>  = withPropsMiddleware(
  async ({
    locale,
    query: { page },
    req: {
      headers: { host },
    },
  }) => {
    const pageParam = page ? parseInt(page as string, 10) : 1;
    const faqPromise = locale ? loadFaq(false, locale, host!, pageParam) : null;

    const bannersZone = getEnv("NEXT_PUBLIC_BANNERS_FAQ_ZONE") as string;
    const bannersPromise = locale ? loadBanners(bannersZone, locale, host!) : null;

    const [faq, banners] = await Promise.all([faqPromise, bannersPromise]);

    if (isFaqPageNotFound(faq, pageParam)) {
      return {
        redirect: { permanent: true, destination: ROUTES.NOT_FOUND },
      };
    }

    const features = locale ? await loadFeatures(locale, host) : null;

    return {
      props: { faq, banners, features, defaultLocale: '', customLocales: [], customLocale: '' },
    };
  }
);

const FaqPage: TWithLayout<FC<Infer<typeof getServerSideProps>>> = ({ faq, banners }) => {
  const { t } = useTranslation();
  return (
    <>
      <LayoutHead title={t("pages-faq-title")} description={t("pages-faq-description")} />
      <LayoutContent sx={{ display: "flex", flexDirection: "column", flex: "1" }}>
        <FullWidthVStack spacing={responsiveStyle({ mobile: "0", desktop: "1rem" })} flex="1">
          <Banners bannersData={banners} isDesktopVisible isMobileVisible />
          <FaqPageList faq={faq} />
        </FullWidthVStack>
      </LayoutContent>
    </>
  );
};

FaqPage.Layout = <Layout />;

export default FaqPage;
